import { useEffect, type ReactNode } from 'react';
import { PS, T } from '../lib/theme';

interface Props {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  /** Max width of the card. Defaults to 480. */
  maxWidth?: number;
}

/**
 * Generic modal. Renders centered over a dimmed backdrop.
 * - Esc closes.
 * - Click on backdrop closes; click on card does not.
 * - Locks body scroll while open.
 * - No portal — z-index 100 is enough above Shell's sticky header (z 10).
 */
export function Modal({ open, onClose, title, children, maxWidth = 480 }: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: PS.white,
          borderRadius: 4,
          maxWidth,
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          // Prevent the page from scrolling when touch-scroll reaches the
          // modal's boundary on iOS Safari.
          overscrollBehavior: 'contain',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
        }}
      >
        {title !== undefined && (
          <header
            style={{
              padding: '16px 20px',
              borderBottom: `1px solid ${PS.borderLight}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <h2 style={{ ...T.cardHeading, margin: 0 }}>{title}</h2>
            <button
              onClick={onClose}
              aria-label="Close"
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: 24,
                lineHeight: 1,
                color: PS.textSec,
                padding: 4,
              }}
            >
              ×
            </button>
          </header>
        )}
        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}
